import { supabase } from "@/integrations/supabase/client";

/**
 * Edge functions check the session with the auth server, while normal data
 * reads only check the token itself. So when a session has been ended
 * (expired in a background tab, or signed out elsewhere) pages keep loading
 * but every function call returns 401.
 *
 * These helpers always send a fresh token, refresh + retry once on 401, and
 * if it is still 401 the session is really over: we clear it locally and send
 * the user to /auth?returnUrl=… instead of leaving a broken page behind.
 */

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

async function freshToken(forceRefresh = false): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return null;
  const expiresSoon = (session.expires_at ?? 0) * 1000 < Date.now() + 60_000;
  if (forceRefresh || expiresSoon) {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error) return null;
    return refreshed.session?.access_token ?? null;
  }
  return session.access_token;
}

let redirecting = false;

/** Session is gone for good: clear it locally and go to the login page. */
export async function handleSessionEnded() {
  if (redirecting || typeof window === "undefined") return;
  redirecting = true;
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // ignore — we redirect anyway
  }
  const here = window.location.pathname + window.location.search;
  if (!window.location.pathname.startsWith("/auth")) {
    window.location.href = `/auth?returnUrl=${encodeURIComponent(here)}`;
  }
}

export class SessionEndedError extends Error {
  constructor() {
    super("Je sessie is verlopen, log opnieuw in");
    this.name = "SessionEndedError";
  }
}

/** fetch() to an edge function with a fresh token; for blob/streaming responses. */
export async function authedFunctionFetch(fn: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = (token: string) =>
    fetch(`${FUNCTIONS_URL}/${fn}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });

  let token = await freshToken();
  if (!token) {
    void handleSessionEnded();
    throw new SessionEndedError();
  }
  let res = await doFetch(token);
  if (res.status === 401) {
    token = await freshToken(true);
    if (!token) {
      void handleSessionEnded();
      throw new SessionEndedError();
    }
    res = await doFetch(token);
    if (res.status === 401) {
      void handleSessionEnded();
      throw new SessionEndedError();
    }
  }
  return res;
}

/** supabase.functions.invoke() with a fresh token and one retry on 401. */
export async function invokeAuthedFunction<T = any>(fn: string, body?: Record<string, unknown> | FormData) {
  const call = (t: string) =>
    supabase.functions.invoke(fn, {
      headers: { Authorization: `Bearer ${t}` },
      ...(body ? { body } : {}),
    });

  let token = await freshToken();
  if (!token) {
    void handleSessionEnded();
    return { data: null as T | null, error: new SessionEndedError(), notSignedIn: true };
  }

  let res = await call(token);
  if ((res.error as any)?.context?.status === 401) {
    token = await freshToken(true);
    if (token) res = await call(token);
    if (!token || (res.error as any)?.context?.status === 401) {
      void handleSessionEnded();
      return { data: null as T | null, error: new SessionEndedError(), notSignedIn: true };
    }
  }
  return { data: res.data as T | null, error: res.error, notSignedIn: false };
}
